import React from 'react';
import PropTypes from 'prop-types';
import promiseRetry from 'promise-retry';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Header from 'components/Header/Header';
import TagList from 'components/TagList/TagList';
import mainActions from 'actions/mainActions/mainActions';
import apiActions from 'actions/apiActions/apiActions';
import facebookInviteFriendsActions from 'actions/facebookInviteFriendsActions/facebookInviteFriendsActions';
import { isRequestInFlight } from 'selectors/httpSelectors/httpSelectors';
import {
  getParticipantPageName,
  getParticipantPageUrl,
  getDonationLocation,
  getEventId,
} from 'selectors/apiSelectors/apiSelectors';
import analytics from 'utils/analytics/analytics';
import { Svg, Loading } from '@jg/components';
import facebookLogoWhite from '@jg/styles/lib/assets/icons/facebook-white.svg';
import Immutable from 'immutable';
import Tools from 'utils/tools/tools';

import './InviteFriends.scss';

export class InviteFriends extends React.Component {
  static propTypes = {
    location: PropTypes.object,
    mainActionsBound: PropTypes.object,
    apiActionsBound: PropTypes.object,
    facebookInviteFriendsActionsBound: PropTypes.object,
    mainStore: PropTypes.object,
    facebookStore: PropTypes.object,
    facebookInviteFriendsStore: PropTypes.object,
    isInviteFriendsRequestInFlight: PropTypes.bool,
    donationLocation: PropTypes.string,
    eventId: PropTypes.number,
  };

  static defaultProps = {
    mainActionsBound: {
      resetProceedToDonationClicked: () => {},
    },
    apiActionsBound: {
      createFundraisingPage: () => {},
    },
    mainStore: Immutable.fromJS({
      participantPromise: Promise.resolve(() => Promise.resolve),
    }),
    facebookInviteFriendsStore: Immutable.fromJS({
      selectedFriends: [],
    }),
    isInviteFriendsRequestInFlight: false,
    donationLocation: '',
    facebookStore: Immutable.Map(),
  };

  state = {
    lastIndexVisible: 20,
  }

  componentDidMount() {
    const { mainStore, apiActionsBound, mainActionsBound } = this.props;

    mainActionsBound.resetProceedToDonationClicked();

    mainStore.get('participantPromise')
      .then(() => {
        this.firePageView();
        promiseRetry(
          (retry) => apiActionsBound.createFundraisingPage().catch(retry),
          {
            retries: 2,
            factor: 1,
            minTimeout: 3000,
            maxTimeout: 3000,
          },
        )
        .then(this.createDonation);
      });

    /* istanbul ignore next */
    if (typeof window !== 'undefined' && typeof window.document.querySelector === 'function') {
      window.document.querySelector('body').addEventListener('click', this.onBodyClick);
      this.friendsListWrapperRef.addEventListener('scroll', this.onFriendsListScroll);
      window.addEventListener('resize', this.onResize);
    }
  }

  componentDidUpdate() {
    const { mainStore, donationLocation } = this.props;

    if (mainStore.get('proceedToDonationClicked') && donationLocation && !this.navigated) {
      this.navigated = true;
      window.location.href = donationLocation;
    }

    this.firePageView();
  }

  componentWillUnmount() {
    /* istanbul ignore next */
    if (typeof window !== 'undefined') {
      window.document.querySelector('body').removeEventListener('click', this.onBodyClick);
      this.friendsListWrapperRef.removeEventListener('scroll', this.onFriendsListScroll);
      window.removeEventListener('resize', this.onResize);
    }

    this.pageViewFired = false;
    this.loggedInPageViewFired = false;
  }

  onResize = Tools.debounce(() => {
    // to recalculate the max height style on the list of friends
    this.forceUpdate();
  }, 200);

  onFriendsListScroll = (event) => {
    if (typeof window !== 'undefined' &&
      event.target.scrollTop + this.friendsListWrapperRef.clientHeight >=
      this.friendsListRef.clientHeight) {
      this.increaseLastIndexVisible();
    }
  }

  onBodyClick = () => {
    /* istanbul ignore next */
    const input = window.document.querySelector('.tag-list__input');
    /* istanbul ignore next */
    if (input && window.innerWidth >= 768) {
      input.focus();
    }
  }

  onProceedToInviteFriendsClick = () => {
    const { facebookInviteFriendsActionsBound } = this.props;
    facebookInviteFriendsActionsBound.fbLogin();
    this.trackClick('invite your friends');
  }

  onSubmitFriendSelection = () => {
    const { mainActionsBound, facebookInviteFriendsStore } = this.props;
    mainActionsBound.confirmFriendsSelection();
    this.trackClick('invite your friends', {
      card_count: facebookInviteFriendsStore.get('selectedFriends').size,
      card_id: 'friends_count',
      page_section: 'match my miles > invite your friends > add friends',
    });
  }

  onSkipSharing = (event) => {
    event.preventDefault();
    const { mainActionsBound, facebookStore, facebookInviteFriendsStore } = this.props;
    mainActionsBound.proceedToDonationClicked();
    const fbFriendsLoaded = facebookStore.get('fbFriendsLoaded');

    const extras = {};

    if (fbFriendsLoaded) {
      extras.card_count = facebookInviteFriendsStore.get('selectedFriends').size;
      extras.card_id = 'friends_count';
      extras.page_section = 'match my miles > invite your friends > add friends';
    }

    this.trackClick('skip sharing', extras);
  }

  setFriendsListWrapperRef = (ref) => { this.friendsListWrapperRef = ref; }

  setFriendsListRef = (ref) => { this.friendsListRef = ref; }

  getSelectedFriendsNames(fbFriendsData) {
    const { facebookInviteFriendsStore } = this.props;

    // todo use normalizr for fb friends response

    return facebookInviteFriendsStore.get('selectedFriends').map((friendId) =>
      fbFriendsData.find((friend) => friend.get('id') === friendId),
    );
  }

  getListMaxHeight = () => {
    let height = window.innerHeight / 3;

    if (height < 120) {
      height = 120;
    }

    return height;
  }

  firePageView = () => {
    const { eventId } = this.props;
    if (!this.pageViewFired && !this.isLoggedIn() && eventId) {
      this.firePartialPageView('match my miles > invite your friends');
      this.pageViewFired = true;
    }
    if (!this.loggedInPageViewFired && this.isLoggedIn()) {
      this.firePartialPageView('match my miles > invite your friends > add friends');
      this.loggedInPageViewFired = true;
    }
  }

  firePartialPageView = (pageSection) => {
    const { eventId, location } = this.props;
    const charityId = Number(location.query.charityId);
    analytics.track({
      page_type: 'custom project page',
      event: 'page view',
      subtype: 'match my miles',
      event_value: 'invite your friends',
      page_section: pageSection,
      activity_id: eventId,
      activity_type: 'event_id',
      content_id: charityId,
      content_type: 'charity_id',
      charity_id: charityId,
      actor_id: location.query.guid,
      actor_sources: 'participant guid',
    });
  };

  trackClick(text, extras = {}) {
    const { eventId, location } = this.props;
    if (eventId) {
      const charityId = Number(location.query.charityId);
      analytics.track({
        page_type: 'custom project page',
        event: 'link click',
        subtype: 'button',
        event_value: text,
        page_section: extras.page_section || 'match my miles > invite your friends',
        activity_id: eventId,
        activity_type: 'event_id',
        content_id: charityId,
        content_type: 'charity_id',
        charity_id: charityId,
        actor_id: location.query.guid,
        actor_sources: 'participant guid',
        ...extras,
      });
    }
  }

  trackFriendSelection(isDeselection, isFiltering = false) {
    const { eventId, location } = this.props;

    const charityId = Number(location.query.charityId);
    analytics.track({
      page_type: 'custom project page',
      event: 'click',
      subtype: isFiltering ? 'drop down' : 'toggle',
      event_value: isDeselection ? 'friend deselected' : 'friend selected',
      page_section: 'match my miles > invite your friends > add friends',
      activity_id: eventId,
      activity_type: 'event_id',
      content_id: charityId,
      content_type: 'charity_id',
      charity_id: charityId,
      actor_id: location.query.guid,
      actor_sources: 'participant guid',
    });
  }

  createDonation = () => {
    const { apiActionsBound } = this.props;
    promiseRetry(
      (retry) => apiActionsBound.createDonation().catch(retry),
      {
        retries: 7,
        factor: 1,
        minTimeout: 2000,
        maxTimeout: 2000,
      },
    );
  }

  increaseLastIndexVisible = Tools.debounce(() => {
    this.setState({ lastIndexVisible: this.state.lastIndexVisible + 20 });
  }, 500);

  toggleFriendSelection = (event) => {
    const { facebookInviteFriendsActionsBound, facebookInviteFriendsStore } = this.props;
    event.preventDefault();
    const friendId = event.currentTarget.getAttribute('data-id');
    facebookInviteFriendsActionsBound.toggleFriendSelection(friendId);
    facebookInviteFriendsActionsBound.filterFriends('');

    const isDeselection = facebookInviteFriendsStore.get('selectedFriends').includes(friendId);
    const isFiltering = !!facebookInviteFriendsStore.get('friendsFilterText', false);

    this.trackFriendSelection(isDeselection, isFiltering);
  }

  addFriendSelectionOnEnterPress = (friendId) => {
    const { facebookInviteFriendsActionsBound } = this.props;
    facebookInviteFriendsActionsBound.toggleFriendSelection(friendId);
    facebookInviteFriendsActionsBound.filterFriends('');

    const isDeselection = false;
    const isFiltering = true;

    this.trackFriendSelection(isDeselection, isFiltering);
  }

  removeSelection = (event) => {
    const { facebookInviteFriendsActionsBound } = this.props;
    event.preventDefault();
    const friendId = event.currentTarget.getAttribute('data-id');
    facebookInviteFriendsActionsBound.removeFriendSelection(friendId);
    facebookInviteFriendsActionsBound.filterFriends('');

    this.trackFriendSelection(true);
  }

  filterFriends = (event) => {
    const { facebookInviteFriendsActionsBound } = this.props;
    facebookInviteFriendsActionsBound.filterFriends(event.currentTarget.value);
    this.trackTyping();
  }

  trackTyping() {
    const { eventId, location } = this.props;

    const charityId = Number(location.query.charityId);
    analytics.track({
      page_type: 'custom project page',
      event: 'click',
      subtype: 'button',
      event_value: 'start typing',
      page_section: 'match my miles > invite your friends > add friends',
      activity_id: eventId,
      activity_type: 'event_id',
      content_id: charityId,
      content_type: 'charity_id',
      charity_id: charityId,
      actor_id: location.query.guid,
      actor_sources: 'participant guid',
    });
  }

  isLoggedIn() {
    const { facebookInviteFriendsStore } = this.props;
    return !!facebookInviteFriendsStore.get('fbAuthResponse');
  }

  render() {
    const {
      mainStore,
      facebookStore,
      facebookInviteFriendsStore,
      isInviteFriendsRequestInFlight,
      facebookInviteFriendsActionsBound,
    } = this.props;

    const fbFriendsDataAll = facebookInviteFriendsStore.get('fbFriendsData', Immutable.List());
    const fbFriendsLoaded = facebookInviteFriendsStore.get('fbFriendsLoaded');

    const fbFriendsData = fbFriendsDataAll.slice(0, this.state.lastIndexVisible);
    const friendsFilterText = facebookInviteFriendsStore.get('friendsFilterText', '');

    const selectedFriends = this.getSelectedFriendsNames(fbFriendsDataAll);
    const selectedFriendsIds = facebookInviteFriendsStore.get('selectedFriends');

    const proceedToDonationClicked = mainStore.get('proceedToDonationClicked');

    return (
      <div className="invite-friends">
        <Header step="2" />
        <div className="invite-friends__body container">
          <p className="invite-friends__nice-choice">Awesome! Invite your friends to match you</p>
          <p className="invite-friends__intro">Ask your friends to match your miles with a pledge</p>

          { !this.isLoggedIn() ? (
            <div>
              { facebookStore.get('fbInitialised') && (
                <div className="invite-friends__continute-btn__wrapper">
                  <button type="button" className="invite-friends__continute-btn" onClick={this.onProceedToInviteFriendsClick}>
                    <Svg markup={facebookLogoWhite} alt="Facebook Icon" className="invite-friends__continute-btn__fb-icon" />
                    Invite Friends
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div>
              { !fbFriendsLoaded && <Loading className="invite-friends__loading-friends" /> }
              { fbFriendsData.size > 0 && (
                <TagList
                  selectedItems={selectedFriends}
                  selectedIds={selectedFriendsIds}
                  removeSelection={this.removeSelection}
                  onChange={this.filterFriends}
                  value={friendsFilterText}
                  allFriends={fbFriendsDataAll}
                  removeFriendSelection={facebookInviteFriendsActionsBound.removeFriendSelection}
                  toggleFriendSelection={this.toggleFriendSelection}
                  addFriendSelectionOnEnterPress={this.addFriendSelectionOnEnterPress}
                  placeholder={selectedFriends.size === 0 ? 'Start typing a friend’s name' : ''}
                />
              )}
              { fbFriendsLoaded && fbFriendsData.size === 0 && (
                <p className="invite-friends__intro">Oh dear! It looks like you don’t have any friends.</p>
              )}
            </div>
          )}

          <div className="invite-friends__list__wrapper" ref={this.setFriendsListWrapperRef} style={{ display: fbFriendsData.size ? 'block' : 'none', maxHeight: this.getListMaxHeight() }}>
            <ul className={`invite-friends__list ${friendsFilterText.length > 0 && '--filtering'}`} ref={this.setFriendsListRef}>
              { fbFriendsData.map((friend) => (
                <li key={friend.get('id')} className={`invite-friends__list__item ${selectedFriendsIds.includes(friend.get('id')) && '--selected'}`}>
                  <a href="" className="invite-friends__list__item__btn" onClick={this.toggleFriendSelection} data-id={friend.get('id')}>
                    <div className="invite-friends__list__item__btn__inner">
                      <img src={friend.getIn(['picture', 'data', 'url'])} alt="" />
                      { friend.get('name') }
                    </div>
                  </a>
                </li>
              ))}
            </ul>
            { this.state.lastIndexVisible + 1 < fbFriendsDataAll.size && (
              <div className="invite-friends__loading-next-friends">
                <Loading size="small" />
              </div>
            )}
          </div>

          { this.isLoggedIn() && fbFriendsData.size > 0 && (
            <div className="invite-friends__continute-btn__wrapper">
              <button type="button" className={`invite-friends__continute-btn ${selectedFriends.size ? '' : 'is-disabled'}`} onClick={this.onSubmitFriendSelection}>
                <Svg markup={facebookLogoWhite} alt="Facebook Icon" className="invite-friends__continute-btn__fb-icon" />

                { isInviteFriendsRequestInFlight || proceedToDonationClicked ?
                  <Loading size="small" colour="white" /> :
                  <span>Invite and Continue</span>
                }
              </button>
            </div>
          )}

          <div className="invite-friends__skip-btn__wrapper">
            { proceedToDonationClicked ?
              <Loading size="small" /> :
              <a href="" onClick={this.onSkipSharing} className="invite-friends__skip-btn">Skip sharing</a>
            }
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  eventId: getEventId(state),
  mainStore: state.mainStore,
  facebookStore: state.facebookStore,
  facebookInviteFriendsStore: state.facebookInviteFriendsStore,
  isInviteFriendsRequestInFlight: isRequestInFlight('inviteFriends')(state),
  participantPageName: getParticipantPageName(state),
  participantPageUrl: getParticipantPageUrl(state),
  donationLocation: getDonationLocation(state),
});

export default connect(
  mapStateToProps,
  (dispatch) => ({
    mainActionsBound: bindActionCreators(mainActions, dispatch),
    apiActionsBound: bindActionCreators(apiActions, dispatch),
    facebookInviteFriendsActionsBound: bindActionCreators(facebookInviteFriendsActions, dispatch),
  }),
)(InviteFriends);



// WEBPACK FOOTER //
// ./layouts/InviteFriends/InviteFriends.js